import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import {
  AppstoreOutlined, ThunderboltOutlined, WalletOutlined, ArrowRightOutlined, ImportOutlined,
} from '@ant-design/icons';

import { ThemeContext } from '../../../context/ThemeContext';
import LandingLayout from '../LandingLayout';
import { STRATEGIES } from './strategyData';
import { usePortfolioUser } from './usePortfolioUser';
import { currency } from './portfolioHoldingPanels';
import apiClient from '../../../services/apiClient';
import './ConstructPortfolioPage.css';
import '../Instruments.css';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const RiskBadge = ({ risk, riskClass }) => (
  <span className={`cp-risk-badge ${riskClass}`}>● {risk}</span>
);

const StrategyCard = ({ strategy, onSelect }) => (
  <div
    className="cp-card"
    onClick={() => onSelect(strategy.route)}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => e.key === 'Enter' && onSelect(strategy.route)}
    aria-label={`Open ${strategy.title} strategy`}
  >
    <div className="cp-card-icon">{strategy.icon}</div>
    <div className="cp-card-title">{strategy.title}</div>
    <div className="cp-card-desc">{strategy.description}</div>
    <div className="cp-card-footer">
      <RiskBadge risk={strategy.risk} riskClass={strategy.riskClass} />
      <button
        type="button"
        className="cp-card-btn"
        id={`construct-btn-${strategy.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(strategy.route);
        }}
      >
        Construct Portfolio →
      </button>
    </div>
  </div>
);

function StrategiesSection({ onSelect }) {
  return (
    <section className="cp-strategies-section">
      <div className="cp-section-header">
        <h2 className="cp-section-title">Choose a Strategy</h2>
      </div>

      <div className="cp-cards-grid">
        {STRATEGIES.map((strategy) => (
          <StrategyCard key={strategy.id} strategy={strategy} onSelect={onSelect} />
        ))}
      </div>
    </section>
  );
}

function ConstructPortfolioPage() {
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { userId, isAuthenticated, loading: userLoading } = usePortfolioUser();

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [preRefreshDone, setPreRefreshDone] = useState(false);
  const [preRefreshLoading, setPreRefreshLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!userId) return;
    try {
      setLoadingStats(true);
      const res = await apiClient.get(`${BASE_URL}/user/portfolio/stats/${userId}`);
      setStats(res.data?.data);
    } catch (e) {
      console.error('Failed to load portfolio stats', e);
    } finally {
      setLoadingStats(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setPreRefreshDone(true);
      setPreRefreshLoading(false);
      setStats(null);
      return;
    }
    const refreshBeforeLoad = async () => {
      try {
        setPreRefreshLoading(true);
        await apiClient.post(`${BASE_URL}/user/portfolio/refresh-prices/${userId}`);
      } catch (e) {
        console.error('Failed to refresh portfolio prices', e);
      } finally {
        setPreRefreshDone(true);
        setPreRefreshLoading(false);
      }
    };
    refreshBeforeLoad();
  }, [userId]);

  useEffect(() => {
    if (preRefreshDone && userId) {
      fetchStats();
    }
  }, [preRefreshDone, fetchStats, userId]);

  const hasHoldings = stats != null && Number(stats.totalCount) > 0;

  return (
    <LandingLayout>
      <div className={`cp-page isin-page${isDark ? ' dark' : ''}`}>
        <div className="container" style={{ paddingBottom: 0 }}>
          <header className="instruments-page-header">
            <h1 className="instruments-page-title">
              <AppstoreOutlined className="instruments-page-icon" />
              Construct Portfolio
            </h1>
            <p className="instruments-page-hint">
              Build a diversified bond portfolio using one of our pre-defined strategies.
            </p>
            <div className="cp-header-actions">
              {isAuthenticated && hasHoldings && (
                <button
                  type="button"
                  className="cp-card-btn cp-my-portfolio-link"
                  onClick={() => navigate('/my-portfolio')}
                >
                  <WalletOutlined /> View My Portfolio <ArrowRightOutlined />
                </button>
              )}
              <button
                type="button"
                className="cp-card-btn cp-risk-builder-link"
                onClick={() => navigate('/fixed-income-portfolio')}
              >
                <ThunderboltOutlined /> AI portfolio builder →
              </button>
              <button
                type="button"
                className="cp-card-btn cp-import-investments-btn"
                onClick={() => navigate('/import-investments')}
              >
                <ImportOutlined /> Import CAS
              </button>
            </div>
          </header>
        </div>

        {(userLoading || (isAuthenticated && preRefreshLoading) || (isAuthenticated && loadingStats && !stats)) && (
          <div className="cp-page-initial-loading">
            <Spin size="large" />
            <p className="cp-page-initial-loading-text">Loading portfolio…</p>
          </div>
        )}

        {!userLoading && !isAuthenticated && preRefreshDone && (
          <div className="container">
            <div className={`cp-portfolio-empty${isDark ? ' cp-portfolio-empty--dark' : ''}`}>
              <p className="cp-portfolio-empty-title">Log in to save holdings</p>
              <p className="cp-portfolio-empty-hint">
                Sign in with your SimplyBond account to add bonds and track them in My Portfolio.
              </p>
              <button type="button" className="cp-card-btn" onClick={() => navigate('/login', { state: { from: '/construct-portfolio' } })}>
                Log in →
              </button>
            </div>
          </div>
        )}

        {preRefreshDone && isAuthenticated && hasHoldings && (
          <div className="container">
            <div className={`cp-portfolio-banner${isDark ? ' cp-portfolio-banner--dark' : ''}`}>
              <div className="cp-portfolio-banner-text">
                <strong>{stats.totalCount} holdings</strong>
                {' · '}
                {currency(stats.totalMarketValue)}
                {' · '}
                avg yield {stats.avgYield}%
              </div>
              <button
                type="button"
                className="cp-card-btn"
                onClick={() => navigate('/my-portfolio')}
              >
                Open portfolio analytics →
              </button>
            </div>
          </div>
        )}

        {preRefreshDone && isAuthenticated && stats && !hasHoldings && (
          <div className="container">
            <div className={`cp-portfolio-empty${isDark ? ' cp-portfolio-empty--dark' : ''}`}>
              <p className="cp-portfolio-empty-title">Portfolio is empty</p>
              <p className="cp-portfolio-empty-hint">
                Add bonds from a strategy below — analytics and holdings will appear in My Portfolio.
              </p>
            </div>
          </div>
        )}

        {preRefreshDone && <StrategiesSection onSelect={navigate} />}
      </div>
    </LandingLayout>
  );
}

export default ConstructPortfolioPage;
